import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import type { Friend, FriendInvite, FeedPost } from '../types/social';

interface SocialStore {
  friends: Friend[];
  pendingRequests: Friend[];
  sentRequests: Friend[];
  sentInvites: FriendInvite[];
  receivedInvites: FriendInvite[];
  feedPosts: FeedPost[];
  loading: boolean;
  error: string | null;
  
  // Feed management
  fetchFeed: () => Promise<void>;
  createPost: (params: {
    content: string;
    recipeId?: string;
    mealId?: string;
    imageUrl?: string;
    taggedUserIds?: string[];
    recipeUrl?: string;
  }) => Promise<void>;
  deletePost: (postId: string) => Promise<void>;
}

export const useSocialStore = create<SocialStore>((set, get) => ({
  friends: [],
  pendingRequests: [],
  sentRequests: [],
  sentInvites: [],
  receivedInvites: [],
  feedPosts: [],
  loading: false,
  error: null,

  fetchFeed: async () => {
    set({ loading: true, error: null });
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        set({ feedPosts: [] });
        return;
      }

      const { data, error } = await supabase
        .from('feed_posts')
        .select(`
          *,
          user_profile:user_profiles!feed_posts_user_id_fkey (
            id,
            email,
            display_name,
            avatar_url
          ),
          recipe:recipes!feed_posts_recipe_id_fkey (
            title,
            image_url
          ),
          meal:meals!feed_posts_meal_id_fkey (
            name
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      set({
        feedPosts: (data || []).map(post => ({
          ...post,
          user_profile: post.user_profile,
          recipe_title: post.recipe?.title,
          image_url: post.recipe?.image_url || post.image_url,
          meal_name: post.meal?.name
        }))
      });
    } catch (err) {
      console.error('Error in fetchFeed:', err);
      set({ 
        feedPosts: [],
        error: 'Failed to fetch feed posts'
      });
    } finally {
      set({ loading: false });
    }
  },

  createPost: async ({ content, recipeId, mealId, imageUrl, taggedUserIds, recipeUrl }) => {
    set({ loading: true, error: null });
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('feed_posts')
        .insert([{
          user_id: user.id,
          content,
          recipe_id: recipeId,
          meal_id: mealId,
          image_url: imageUrl,
          tagged_users: taggedUserIds || [],
          source_url: recipeUrl
        }]);

      if (error) throw error;
      await get().fetchFeed();
    } catch (err) {
      console.error('Error creating post:', err);
      set({ error: err instanceof Error ? err.message : 'Failed to create post' });
      throw err;
    } finally {
      set({ loading: false });
    }
  },

  deletePost: async (postId: string) => {
    set({ loading: true, error: null });
    try {
      const { error } = await supabase
        .from('feed_posts')
        .delete()
        .eq('id', postId);

      if (error) throw error;
      await get().fetchFeed();
    } catch (err) {
      console.error('Error deleting post:', err);
      set({ error: err instanceof Error ? err.message : 'Failed to delete post' });
    } finally {
      set({ loading: false });
    }
  }
}));